import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Home, X, ClipboardList } from 'lucide-react';
import {
  getRoundLeaderboard,
  getMyRoundDetail,
  type MyAnswerDetail,
  type MyRoundDetail,
} from '@/lib/api/endpoints/leaderboard';
import { queryKeys } from '@/lib/api/queryKeys';
import { gameStore, type LiveScoreboardEntry, type WordChainRevealData } from '@/store/gameStore';
import { Button } from '@/design-system/components/Button';
import { Avatar } from '@/design-system/components/Avatar';
import { t } from '@/lib/i18n';
import styles from './ResultPage.module.css';

export default function ResultPage() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(false);

  if (!roundId) throw new Error('Missing roundId');

  const finalLeaderboard = gameStore((s) => s.finalLeaderboard);
  const myParticipantId = gameStore((s) => s.myParticipantId);
  const wordChainRevealData = gameStore((s) => s.wordChainRevealData);

  // Fallback: fetch from REST if socket state is gone (e.g., page refresh)
  const { data: restLeaderboard } = useQuery({
    queryKey: queryKeys.leaderboard.round(roundId),
    queryFn: () => getRoundLeaderboard(roundId),
    enabled: finalLeaderboard.length === 0,
  });

  const leaderboard: LiveScoreboardEntry[] = finalLeaderboard.length > 0
    ? finalLeaderboard
    : ((restLeaderboard ?? []) as LiveScoreboardEntry[]);

  const myEntry = leaderboard.find((e) => e.participant_id === myParticipantId);
  const myRank = myEntry
    ? leaderboard.findIndex((e) => e.participant_id === myParticipantId) + 1
    : null;

  const handleGoLobby = () => {
    gameStore.getState().reset();
    navigate('/lobby', { replace: true });
  };

  const top3 = leaderboard.slice(0, 3);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {/* Title */}
        <div className={styles.header}>
          <Trophy size={40} color="var(--color-warning)" />
          <h1 className={styles.title}>{t('result.pageTitle')}</h1>
        </div>

        {/* My result */}
        {myEntry && (
          <div className={styles.myResult}>
            <div className={styles.myRank}>{t('result.rank', { rank: myRank })}</div>
            <div className={styles.myScore}>{myEntry.total_score.toLocaleString('vi-VN')}</div>
            <div className={styles.myScoreLabel}>{t('result.scoreLabel')}</div>
            {/* Word chain: show my per-word result */}
            {wordChainRevealData ? (() => {
              const mine = wordChainRevealData.per_player_result.find(r => r.participant_id === myParticipantId);
              return mine ? (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' }}>
                  {mine.correct_mask.map((ok, i) => (
                    <span key={i} style={{
                      padding: '4px 10px', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
                      background: ok ? 'var(--color-success-bg,#d4edda)' : 'var(--color-danger-bg,#f8d7da)',
                      color: ok ? 'var(--color-success,#155724)' : 'var(--color-danger,#721c24)',
                    }}>
                      {mine.submitted_words[i] || '(trống)'} {ok ? '✓' : '✗'}
                    </span>
                  ))}
                </div>
              ) : null;
            })() : myEntry.correct_count != null && (
              <div className={styles.myCorrect}>{t('result.correctCount', { n: myEntry.correct_count })}</div>
            )}
            {!wordChainRevealData && (
              <button className={styles.detailBtn} onClick={() => setShowDetail(true)}>
                <ClipboardList size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {t('result.detailButton')}
              </button>
            )}
          </div>
        )}

        {/* Podium top 3 */}
        {top3.length > 0 && (
          <div className={styles.podium}>
            {top3.length >= 2 && <PodiumItem entry={top3[1]} place={2} />}
            <PodiumItem entry={top3[0]} place={1} />
            {top3.length >= 3 && <PodiumItem entry={top3[2]} place={3} />}
          </div>
        )}

        {/* Word Chain: full per-player results */}
        {wordChainRevealData && (
          <div style={{ width: '100%', maxWidth: '520px' }}>
            <div className={styles.lbTitle} style={{ marginBottom: '6px' }}>
              Kết quả chi tiết
            </div>
            <p style={{ textAlign: 'center', fontSize: '14px', fontWeight: 600, color: 'var(--color-primary,#1a73e8)', marginBottom: '12px' }}>
              {wordChainRevealData.correct_words.join(' → ')}
            </p>
            {wordChainRevealData.per_player_result
              .sort((a, b) => a.rank - b.rank)
              .map((r) => {
                const isMe = r.participant_id === myParticipantId;
                const correct = r.correct_mask.filter(Boolean).length;
                return (
                  <div key={r.participant_id} style={{
                    display: 'flex', flexDirection: 'column', gap: '6px',
                    padding: '10px 12px', marginBottom: '8px', borderRadius: '10px',
                    background: isMe ? 'var(--color-primary-subtle,#e8f0fe)' : 'var(--color-surface-raised,#f5f5f5)',
                    border: isMe ? '2px solid var(--color-primary,#1a73e8)' : '2px solid transparent',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        width: '22px', height: '22px', borderRadius: '50%',
                        background: 'var(--color-primary,#1a73e8)', color: '#fff',
                        fontWeight: 700, fontSize: '11px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>#{r.rank}</span>
                      <Avatar src={r.avatar_url ?? undefined} name={r.display_name ?? '?'} size="sm" />
                      <span style={{ flex: 1, fontWeight: 600, fontSize: '14px' }}>
                        {r.display_name ?? t('result.fallbackPlayerName')}{isMe && ' 👤'}
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                        {correct}/{r.correct_mask.length} từ đúng
                      </span>
                      <span style={{ fontWeight: 700, color: 'var(--color-success,#28a745)' }}>
                        +{r.score_awarded.toLocaleString('vi-VN')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', paddingLeft: '30px' }}>
                      {r.correct_mask.map((ok, wi) => (
                        <span key={wi} style={{
                          padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                          background: ok ? 'var(--color-success-bg,#d4edda)' : 'var(--color-danger-bg,#f8d7da)',
                          color: ok ? 'var(--color-success,#155724)' : 'var(--color-danger,#721c24)',
                        }}>
                          {r.submitted_words[wi] || '(trống)'} {ok ? '✓' : '✗'}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Full leaderboard */}
        {leaderboard.length > 0 && (
          <div className={styles.fullLb}>
            <div className={styles.lbTitle}>{t('result.leaderboardTitle')}</div>
            <div className={styles.lbList}>
              {leaderboard.map((entry, i) => {
                const isMe = entry.participant_id === myParticipantId;
                return (
                  <div
                    key={entry.participant_id}
                    className={`${styles.lbRow} ${isMe ? styles.lbRowMe : ''}`}
                  >
                    <span className={styles.lbRank}>{i + 1}</span>
                    <Avatar
                      src={entry.avatar_url ?? undefined}
                      name={entry.display_name ?? '?'}
                      size="sm"
                    />
                    <span className={styles.lbName}>
                      {entry.display_name ?? t('result.fallbackPlayerName')}
                      {isMe && ' 👤'}
                    </span>
                    {!wordChainRevealData && entry.correct_count != null && (
                      <span className={styles.lbCorrect}>{entry.correct_count}&#x2713;</span>
                    )}
                    <span className={styles.lbScore}>
                      {entry.total_score.toLocaleString('vi-VN')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Button leftIcon={<Home size={16} />} onClick={handleGoLobby}>
          {t('result.backToLobby')}
        </Button>
      </div>

      {/* My Detail Drawer */}
      {showDetail && (
        <MyDetailDrawer
          roundId={roundId}
          onClose={() => setShowDetail(false)}
        />
      )}
    </div>
  );
}

// ---- Sub-components ----

function PodiumItem({ entry, place }: { entry: LiveScoreboardEntry; place: number }) {
  const medals = ['', '🥇', '🥈', '🥉'];
  return (
    <div className={`${styles.podiumItem} ${styles[`place${place}`]}`}>
      <div className={styles.podiumMedal}>{medals[place]}</div>
      <Avatar src={entry.avatar_url ?? undefined} name={entry.display_name ?? '?'} size={place === 1 ? 'lg' : 'md'} />
      <div className={styles.podiumName}>{entry.display_name ?? t('result.fallbackPlayerName')}</div>
      <div className={styles.podiumScore}>{entry.total_score.toLocaleString('vi-VN')}</div>
    </div>
  );
}

function MyDetailDrawer({ roundId, onClose }: { roundId: string; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.me.roundDetail(roundId),
    queryFn: () => getMyRoundDetail(roundId),
  });

  return (
    <div className={styles.drawerOverlay} onClick={onClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <span className={styles.drawerTitle}>{t('result.drawerTitle')}</span>
          <button className={styles.drawerClose} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {isLoading && <div className={styles.drawerLoading}>{t('result.drawerLoading')}</div>}
        {isError && <div className={styles.drawerError}>{t('result.drawerError')}</div>}

        {data && (
          <>
            <div className={styles.drawerSummary}>
              <span>{t('result.drawerRank', { rank: data.final_rank ?? '-' })}</span>
              <span>{t('result.drawerScore', { score: data.total_score.toLocaleString('vi-VN') })}</span>
              <span>{t('result.drawerCorrect', { correct: data.correct_count, total: data.total_questions })}</span>
              {data.avg_response_time_ms != null && (
                <span>{t('result.avgTime', { n: (data.avg_response_time_ms / 1000).toFixed(1) })}</span>
              )}
            </div>

            <div className={styles.answerList}>
              {data.answers.map((a: MyAnswerDetail) => (
                <div
                  key={a.order_index}
                  className={`${styles.answerItem} ${
                    a.result === 'correct'
                      ? styles.answerCorrect
                      : a.result === 'timeout'
                      ? styles.answerTimeout
                      : styles.answerWrong
                  }`}
                >
                  <div className={styles.answerQ}>
                    <span className={styles.answerIdx}>#{a.order_index}</span>
                    <span className={styles.answerText}>{a.question_text}</span>
                  </div>
                  <div className={styles.answerRow}>
                    <span className={styles.answerLabel}>{t('result.answerLabel')}</span>
                    <span className={styles.answerVal}>
                      {a.raw_answer ?? <em>{t('result.noAnswer')}</em>}
                    </span>
                  </div>
                  <div className={styles.answerRow}>
                    <span className={styles.answerLabel}>{t('result.correctAnswerLabel')}</span>
                    <span className={styles.answerVal}>{a.canonical_answer}</span>
                  </div>
                  <div className={styles.answerMeta}>
                    {t('result.answerScore', { score: `${a.result === 'correct' ? '+' : ''}${a.score_awarded.toLocaleString('vi-VN')}` })}
                    {a.response_time_ms != null && ` · ${(a.response_time_ms / 1000).toFixed(1)}s`}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
